import classNames from "classnames";
import type {
  MRT_ColumnDef,
  MRT_VisibilityState
} from "mantine-react-table-open";
import { MantineRoundedNumberCell } from "../../../../../mantine/mrt/components/cell/MantineRoundedNumberCell";
import type {
  EnergyPriceUnits,
  EnergyRevenuesListItem
} from "../../../RevenueMonitoring.types";
import { CustomTableHeader } from "../CustomTableCell/CustomTableHeader";
import { NullCell } from "../CustomTableCell/NullCell";
import type { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable";
import { getEnergyUnitHeaderText } from "../EnergyRevenuesTable";
import { isFirstVisibleColumn } from "../utils/isFirstVisibleColumn";

type MarktwertColumnName = Extract<
  keyof typeof REVENUE_TABLE_HEADER_TEXTS,
  | "monatsmarktwert_solar"
  | "monatsmarktwert_wind"
  | "jahresmarktwert_solar"
  | "jahresmarktwert_wind"
>;

interface GetMarktwertColumnParams {
  columnName: MarktwertColumnName;
  visibilityLabel: string;
  columnVisibility: MRT_VisibilityState;
  energyPriceUnit: EnergyPriceUnits;
  groupColumns: ReadonlyArray<string>;
}

export function getMarktwertColumn({
  columnName,
  visibilityLabel,
  columnVisibility,
  energyPriceUnit,
  groupColumns
}: GetMarktwertColumnParams): MRT_ColumnDef<EnergyRevenuesListItem> {
  const isFirstColumn = isFirstVisibleColumn(
    columnName,
    columnVisibility,
    groupColumns
  );

  return {
    accessorKey: columnName,
    header: getEnergyUnitHeaderText(columnName, energyPriceUnit),
    size: 200,
    meta: { visibilityLabel },
    mantineTableHeadCellProps: {
      className: classNames({ "column-header-border-left": isFirstColumn })
    },
    mantineTableBodyCellProps: {
      className: classNames({ "column-body-border-left": isFirstColumn })
    },
    mantineTableFooterCellProps: {
      className: classNames({ "column-body-border-left": isFirstColumn })
    },
    Header: CustomTableHeader,
    Cell: ({ cell }) => {
      const value = cell.getValue<number | null | undefined>();

      if (value === undefined) {
        return null;
      }

      if (value === null) {
        return <NullCell />;
      }

      return <MantineRoundedNumberCell decimalScale={2} value={value} />;
    }
  };
}
